import { Base64 } from 'js-base64';
import { clearTinkReportCookies, TINK_REPORT_PARAM } from 'utils/tinkReports';
import { getFetchHeaders } from 'utils/fetchHeaders';
import { ROUTES } from 'utils/constants/constants_routes';
import { SESSION_TINK_REASON } from 'utils/constants';
import { showNotification } from 'components/GlobalNotification/GlobalNotification';
import { useCallback, useEffect, useRef, useTransition } from 'react';
import { useErrorManager } from 'InternalLibrary/app';
import { useGetClientApplication } from 'api';
import { useNavigate } from 'react-router-dom';
import smartlookClient from 'smartlook-client';
import useTink from 'hooks/useTink';

export default function useTinkWaiting() {
  const navigate = useNavigate();
  const { sendReportIds } = useTink();
  const { setError } = useErrorManager();
  const { fetch: fetchGetApplication } = useGetClientApplication();
  const [isProcessing, startProcessing] = useTransition();

  const hasProcessed = useRef(false);

  const checkParams = useCallback(() => {
    const params = new URLSearchParams(window.location.search);

    const transaction_report_id = params.get(TINK_REPORT_PARAM.TRANSACTION);
    const risk_insights_report_id = params.get(TINK_REPORT_PARAM.RISK_INSIGHTS);
    const reports_generation_job_id = params.get(TINK_REPORT_PARAM.JOB_ID);
    const {
      error: errorStatus,
      error_reason: errorReason,
      tracking_id: trackingId,
    } = Object.fromEntries(params);

    const hasError = Boolean(errorStatus || errorReason);
    const tinkSessionId =
      reports_generation_job_id ||
      transaction_report_id ||
      risk_insights_report_id;

    // Reload once on first load to ensure a fresh state (cache fix)
    const tinkReloadKey = tinkSessionId ? `tink_reload_${tinkSessionId}` : null;
    if (tinkReloadKey && !sessionStorage.getItem(tinkReloadKey)) {
      sessionStorage.setItem(tinkReloadKey, 'true');
      window.location.reload();
      return;
    }

    // Error from Tink
    if (hasError) {
      smartlookClient.track('TINK_QUERY_PARAMS_ERROR', {
        error: errorStatus ?? '',
        error_reason: errorReason ?? '',
        tracking_id: trackingId ?? '',
      });

      // Errors with a dedicated destination screen — navigate directly
      if (errorReason === 'REQUEST_FAILED_CREATE_REPORT') {
        setError('default'); // error from 3rd party - tink
        sessionStorage.setItem(SESSION_TINK_REASON, Base64.encode(errorReason));
        navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_ADD_DOCUMENTS);
        return;
      }
      if (errorReason === 'INACTIVITY_OPEN_APPLICATION_FRAUD_EVALUATED') {
        navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_ADD_DOCUMENTS);
        return;
      }

      // All other errors (USER_CANCELLED, INTERNAL_ERROR, etc.) → redirect to
      // the verification screen with the original params so the modal can show
      navigate(`${ROUTES.FIRST_LOAN_ONLINE_BANK_VERIFY_NEW}?${params}`);
      return;
    }

    // Report IDs received → process and navigate to check screen
    showNotification(';');
    startProcessing(async () => {
      smartlookClient.track('TINK_QUERY_PARAMS_SUCCESS', {
        transaction_report_id: transaction_report_id ?? '',
        risk_insights_report_id: risk_insights_report_id ?? '',
        reportGenerationId: reports_generation_job_id ?? '',
        tracking_id: trackingId ?? '',
      });

      let loanApplicationId: number | undefined;
      try {
        const application = await fetchGetApplication({
          headers: getFetchHeaders({
            Accept: 'application/vnd.4finance.web.v1.hal+json',
            'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
          }),
        });
        loanApplicationId = application?.id;
      } catch {
        // Silent — sendReportIds can still proceed without the ID
      }

      smartlookClient.track('CHECK_TINK_QUERY_PARAMS', {
        isTinkFinished: true,
        reportGenearionId: reports_generation_job_id ?? '',
        loanApplicationId: `${loanApplicationId}`,
      });

      showNotification(':');
      try {
        clearTinkReportCookies();
        await sendReportIds({
          loanApplicationId,
          transactionReportId: transaction_report_id ?? '',
          riskInsightsId: risk_insights_report_id ?? '',
          reportGenerationId: reports_generation_job_id ?? '',
        });
      } catch {
        showNotification('..');
        smartlookClient.track('TINK_QUERY_PARAMS_CATCH_ERROR', {});
        // Navigation should not be blocked by a failed report call
      }
      showNotification('.');

      navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_CHECK);
    });
  }, []);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    checkParams();
  }, []);

  return { isProcessing, checkParams };
}
